import type { ApiError } from '@porotutu/react/errors'
import {
  keepPreviousData,
  mutationOptions,
  type QueryClient,
  queryOptions,
} from '@tanstack/react-query'
import { deleteMutationFor, historyQueries } from './history'
import { lockedBodyOf } from './locking'
import type { CollectionRoutes, ReadWriteRoutes, RecordRoutes } from './routes'
import { unwrap } from './unwrap'

const LIST_STALE = 15_000
export const RECORD_STALE = 30_000

export const staleAfterWrite =
  (
    queryClient: QueryClient,
    prefix: readonly string[],
    alsoStale: ReadonlyArray<readonly string[]> = [],
  ) =>
  () =>
    Promise.all(
      [prefix, ...alsoStale].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    )

export type SliceOptions = {
  prefix: readonly string[]
  alsoStale?: ReadonlyArray<readonly string[]>
}

export function readWriteQueries<
  Row extends { id: string },
  Write,
  Query extends Record<string, unknown>,
>({
  prefix,
  routes,
  alsoStale = [],
}: SliceOptions & { routes: ReadWriteRoutes<Row, Write, Query> }) {
  const stale = (queryClient: QueryClient) => staleAfterWrite(queryClient, prefix, alsoStale)

  const listQuery = (query: Query) =>
    queryOptions({
      queryKey: [...prefix, 'list', query] as const,
      queryFn: async () => unwrap(await routes.get({ query })),
      placeholderData: keepPreviousData,
      staleTime: LIST_STALE,
    })

  const recordQuery = (id: string) =>
    queryOptions({
      queryKey: [...prefix, 'record', id] as const,
      queryFn: async () => unwrap(await routes({ id }).get()),
      staleTime: RECORD_STALE,
    })

  const seed = (queryClient: QueryClient, row: Row) => {
    queryClient.setQueryData(recordQuery(row.id).queryKey, row)
  }

  const updateMutation = (queryClient: QueryClient, record: Row) =>
    mutationOptions<Row, ApiError, Write>({
      mutationKey: [...prefix, 'update', record.id],
      mutationFn: async (body) =>
        unwrap(await routes({ id: record.id }).put(lockedBodyOf(body, record))),
      onSuccess: (row) => seed(queryClient, row),
      onSettled: stale(queryClient),
    })

  return { listQuery, recordQuery, updateMutation, seed, stale }
}

export function collectionQueries<
  Row extends { id: string },
  Write,
  Create,
  Query extends Record<string, unknown>,
>(options: SliceOptions & { routes: CollectionRoutes<Row, Write, Create, Query> }) {
  const { routes, prefix } = options
  const composed = readWriteQueries<Row, Write, Query>(options)

  const createMutation = (queryClient: QueryClient) =>
    mutationOptions<Row, ApiError, Create>({
      mutationKey: [...prefix, 'create'],
      mutationFn: async (body) => unwrap(await routes.post(body)),
      onSuccess: (row) => composed.seed(queryClient, row),
      onSettled: composed.stale(queryClient),
    })

  return { ...composed, createMutation }
}

export function crudQueries<
  Row extends { id: string },
  Entry,
  Write,
  Create,
  Query extends Record<string, unknown>,
>(options: SliceOptions & { routes: RecordRoutes<Row, Entry, Write, Create, Query> }) {
  const collection = collectionQueries<Row, Write, Create, Query>(options)

  return {
    ...collection,
    historyQuery: historyQueries<Entry>(options),
    deleteMutation: deleteMutationFor<Row>(options),
  }
}
